// V2d driver-only tracking screen. Orchestrates the booking fetch, progress
// state, geolocation, and all driver actions. Layout follows the V2d
// handoff: GeoWarning banner → violet Hero → scrollable card column →
// pinned bottom CTA. Client view is not rendered here — the parent
// `screen/Track.js` falls back to the legacy `trackContent.js` in that
// case.
//
// All network calls reuse the existing `/api/v1/driverapp/track/*`
// endpoints. No backend changes were made for this redesign.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import "moment/locale/fr";

import { apiRoot } from "../../../../index";
import { convertBookingObj } from "../../../../store/actions/bookingActions";
import { startLoading, stopLoading } from "../../../../utils/loader";
import { driverAppUrlFromTrack } from "../../../../utils/driverApp/session";

import { tokens } from "./tokens";
import { Icon, ICON_PATHS } from "./icons";
import {
  bookingProgressStatusActionList,
  bookingProgressStatusList,
} from "./progress";
import { useWatchPosition } from "./useWatchPosition";
import GeoWarning from "./GeoWarning";
import UpcomingBookings from "./UpcomingBookings";
import {
  BonDeMissionSheet,
  CommentSheet,
  ConfirmSheet,
  UpdateDriverSheet,
} from "./sheets";

// ────────────────────────────────────────────────────────────────
// La Première CDG detection — strings must match what the legacy
// trackContent.js used so a given booking renders the same callout.
// ────────────────────────────────────────────────────────────────

function isLaPremiereCdgName(name) {
  return /La Premiere/i.test(name);
}
function isCdgAddress(addr) {
  return /CDG/.test(addr) || /A[eé]roport.*Charles.*de.*Gaulle/i.test(addr);
}

// ────────────────────────────────────────────────────────────────
// Chrome primitives — kept local because they're only ever used here.
// ────────────────────────────────────────────────────────────────

const PageBg = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  /* dvh handles mobile browser chrome (address bar) correctly; fallback to vh */
  height: 100vh;
  height: 100dvh;
  background: ${tokens.colors.page};
  color: ${tokens.colors.text};
  font-family: ${tokens.typo.family};
`;

const Column = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 140px;
`;

const CardsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacings.gapCards}px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${tokens.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 6px 4px 0;
`;

const Card = styled.div`
  background: ${tokens.colors.surface};
  border-radius: ${tokens.radii.md}px;
  border: 1px solid ${tokens.colors.border};
  box-shadow: ${tokens.shadows.card};
`;

// ────────────────────────────────────────────────────────────────
// Hero — violet gradient band, state pill mirrors progress_status.
// ────────────────────────────────────────────────────────────────

const HeroBand = styled.div`
  background: linear-gradient(
    180deg,
    ${tokens.colors.violetDeep} 0%,
    ${tokens.colors.violet} 100%
  );
  color: #fff;
  padding: 16px 22px 22px;
  border-bottom-left-radius: ${tokens.radii.lg}px;
  border-bottom-right-radius: ${tokens.radii.lg}px;
`;

const HeroTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  width: 34px;
  height: 34px;
  border-radius: ${tokens.radii.sm}px;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Lien vers l'espace chauffeur (liste de toutes ses courses). Ouvre une
// session à partir du jeton de suivi si le chauffeur n'en a pas encore.
const AppLink = styled.a`
  background: rgba(255, 255, 255, 0.15);
  border-radius: ${tokens.radii.pill}px;
  color: #fff;
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
  padding: 8px 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.18);
  padding: 5px 10px;
  border-radius: ${tokens.radii.pill}px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: ${tokens.radii.pill}px;
  background: ${tokens.colors.successDot};
  box-shadow: 0 0 0 3px ${tokens.colors.successHalo};
`;

function Hero({ statusText, pickupHHmm, pickupLongDate, flightNumber, onBack, appUrl }) {
  return (
    <HeroBand>
      <HeroTopRow>
        <BackButton onClick={onBack} aria-label="Retour">
          <Icon path={ICON_PATHS.chevronLeft} size={18} />
        </BackButton>
        {appUrl ? (
          <AppLink href={appUrl} data-testid="track-app-link">
            <Icon path={ICON_PATHS.car} size={14} sw={2.2} />
            Mes courses
          </AppLink>
        ) : (
          <div style={{ width: 34 }} />
        )}
      </HeroTopRow>
      <StatusPill>
        <StatusDot />
        {statusText}
      </StatusPill>
      <div style={{ fontSize: 14, opacity: 0.85 }}>Départ prévu à</div>
      <div
        style={{
          fontSize: 40,
          fontWeight: 700,
          letterSpacing: -1.5,
          lineHeight: 1,
        }}
      >
        {pickupHHmm}
      </div>
      <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>
        {pickupLongDate}
        {flightNumber ? ` · vol ${flightNumber}` : ""}
      </div>
    </HeroBand>
  );
}

// ────────────────────────────────────────────────────────────────
// ClientCard — driver sees client identity at a glance.
// ────────────────────────────────────────────────────────────────

const AvatarCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${tokens.radii.pill}px;
  background: linear-gradient(135deg, #fbcfe8, #c4b5fd);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${tokens.colors.violetInk};
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
`;

function initialsOf(first = "", last = "") {
  return `${(first[0] || "").toUpperCase()}${(last[0] || "").toUpperCase()}`;
}

function ClientCard({ firstname, lastname, phone }) {
  return (
    <Card style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 10 }}>
      <AvatarCircle>{initialsOf(firstname, lastname)}</AvatarCircle>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {firstname} {lastname}
        </div>
        <div style={{ fontSize: 11, color: tokens.colors.muted }}>
          {phone || "N/A"}
        </div>
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────
// TripCard with optional La Première callouts.
// ────────────────────────────────────────────────────────────────

const TripLayout = styled.div`
  display: flex;
  gap: 10px;
`;

const TripRail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 5px;
`;

const PickupDot = styled.div`
  width: 9px;
  height: 9px;
  border-radius: ${tokens.radii.pill}px;
  border: 2px solid ${tokens.colors.violet};
  background: #fff;
`;

const TripLine = styled.div`
  width: 2px;
  flex: 1;
  min-height: 22px;
  background: repeating-linear-gradient(
    to bottom,
    ${tokens.colors.violet} 0 3px,
    transparent 3px 6px
  );
`;

const DropoffDiamond = styled.div`
  width: 9px;
  height: 9px;
  background: ${tokens.colors.violet};
  transform: rotate(45deg);
`;

const TripColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AddressText = styled.div`
  font-size: 13px;
  color: ${tokens.colors.text};
  line-height: 1.35;
`;

const LaPremiereBox = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: ${tokens.radii.md}px;
  background: rgba(139, 92, 246, 0.06);
  border-left: 3px solid ${tokens.colors.violet};
  font-size: 12.5px;
  line-height: 1.4;
  color: ${tokens.colors.violetInk};
  font-weight: 500;
`;

function LaPremiereCallout({ kind }) {
  const text =
    kind === "pickup"
      ? "Arrivée au Terminal 2E : prise en charge porte 17, parvis rouge – Air France La Première. Si le vol atterrit à un autre terminal (rare), rendez-vous à l'accueil tablette."
      : "Pour les départs depuis le Terminal 2E, dépose à la porte 17 sur le parvis rouge dédié à Air France La Première.";
  return (
    <LaPremiereBox>
      <strong>
        La Première · {kind === "pickup" ? "Prise en charge" : "Dépose"}
      </strong>
      <br />
      {text}
    </LaPremiereBox>
  );
}

function TripCard({ pickup, dropoff, isLaPremierePickup, isLaPremiereDropoff }) {
  return (
    <Card style={{ padding: "12px 14px" }}>
      <TripLayout>
        <TripRail>
          <PickupDot />
          <TripLine />
          <DropoffDiamond />
        </TripRail>
        <TripColumn>
          <div>
            <AddressText>{pickup}</AddressText>
            {isLaPremierePickup && <LaPremiereCallout kind="pickup" />}
          </div>
          <div>
            <AddressText>{dropoff}</AddressText>
            {isLaPremiereDropoff && <LaPremiereCallout kind="dropoff" />}
          </div>
        </TripColumn>
      </TripLayout>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────
// InstructionsCard (booking.additional_info) — driver-only, hidden when empty.
// ────────────────────────────────────────────────────────────────

function InstructionsCard({ text }) {
  if (!text || !text.trim()) return null;
  return (
    <Card
      style={{
        padding: "12px 14px",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: tokens.radii.sm,
          background: "rgba(139,92,246,0.1)",
          color: tokens.colors.violetDeep,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon path={ICON_PATHS.note} size={18} sw={2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            color: tokens.colors.muted,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          Informations
        </div>
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.4,
            marginTop: 2,
            whiteSpace: "pre-wrap",
          }}
        >
          {text}
        </div>
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────
// VehicleCard
// ────────────────────────────────────────────────────────────────

function VehicleCard({ vehicle }) {
  return (
    <Card
      style={{
        padding: "12px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: tokens.radii.sm,
          background: "rgba(139,92,246,0.1)",
          color: tokens.colors.violetDeep,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon path={ICON_PATHS.car} size={18} sw={2} />
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 11,
            color: tokens.colors.muted,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.3,
          }}
        >
          Véhicule
        </div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{vehicle || "N/A"}</div>
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────
// Chip + ActionsChips
// ────────────────────────────────────────────────────────────────

const ChipButton = styled.button`
  position: relative;
  padding: 9px 12px;
  border-radius: ${tokens.radii.pill}px;
  border: 1px solid;
  font-size: 12.5px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  font-family: inherit;
  transition: transform 120ms ease;

  &:active {
    transform: scale(0.98);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  background: ${(p) => p.$bg};
  border-color: ${(p) => p.$border};
  color: ${(p) => p.$fg};
`;

const ChipBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 4px;
  width: 7px;
  height: 7px;
  border-radius: ${tokens.radii.pill}px;
  background: ${tokens.colors.violet};
`;

function Chip({ iconPath, label, tone = "default", disabled, onClick, badge }) {
  const tones = {
    default: {
      bg: tokens.colors.surface,
      border: tokens.colors.border,
      fg: tokens.colors.text,
    },
    warn: {
      bg: tokens.colors.warnTint,
      border: tokens.colors.warnBorder,
      fg: tokens.colors.warnInk,
    },
    danger: {
      bg: tokens.colors.dangerTint,
      border: tokens.colors.dangerBorder,
      fg: tokens.colors.danger,
    },
  };
  const t = tones[tone];
  return (
    <ChipButton
      onClick={onClick}
      disabled={disabled}
      $bg={t.bg}
      $border={t.border}
      $fg={t.fg}
    >
      <Icon path={iconPath} size={15} sw={2.2} />
      {label}
      {badge && <ChipBadge />}
    </ChipButton>
  );
}

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${tokens.spacings.gapChips}px;
`;

function ActionsChips({
  hasAvailableDrivers,
  noShowActive,
  updating,
  dirtyComment,
  onStop,
  onNoShow,
  onSwap,
  onComment,
  onCancel,
  onBonDeMission,
  calendarUrl,
}) {
  return (
    <>
      <SectionLabel>Actions rapides</SectionLabel>
      <ChipsRow>
        <Chip
          iconPath={ICON_PATHS.loupe}
          label="Bon de mission"
          onClick={onBonDeMission}
        />
        {calendarUrl && (
          <Chip
            iconPath={ICON_PATHS.calendar}
            label="Ajouter à l'agenda"
            onClick={() => {
              window.location.href = calendarUrl;
            }}
          />
        )}
        <Chip
          iconPath={ICON_PATHS.stop}
          label="Signaler un arrêt"
          tone="warn"
          onClick={onStop}
          disabled={updating}
        />
        <Chip
          iconPath={noShowActive ? ICON_PATHS.check : ICON_PATHS.ghost}
          label={noShowActive ? "'No Show' signalé" : "No Show"}
          tone="warn"
          onClick={onNoShow}
          disabled={updating || noShowActive}
        />
        {hasAvailableDrivers && (
          <Chip
            iconPath={ICON_PATHS.swap}
            label="Changer chauffeur"
            onClick={onSwap}
            disabled={updating}
          />
        )}
        <Chip
          iconPath={ICON_PATHS.note}
          label="Commentaires"
          onClick={onComment}
          badge={dirtyComment}
        />
        <Chip
          iconPath={ICON_PATHS.close}
          label="Annuler la course"
          tone="danger"
          onClick={onCancel}
          disabled={updating}
        />
      </ChipsRow>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// CTAFooter — progress state cycler, label mirrors the action list.
// ────────────────────────────────────────────────────────────────

const FooterBar = styled.div`
  /* Fixed (not absolute) so the CTA sticks to the visible viewport even when
     the mobile browser chrome pushes 100vh past the fold. */
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-top: 1px solid ${tokens.colors.border};
  padding: 12px 16px calc(36px + env(safe-area-inset-bottom));
  z-index: 50;
`;

const FooterInner = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
`;

const CtaButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: ${tokens.radii.md}px;
  background: ${(p) => (p.$done ? "#C4B5FD" : tokens.colors.violet)};
  color: #fff;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: ${(p) => (p.$done ? "none" : tokens.shadows.cta)};
  opacity: ${(p) => (p.disabled && !p.$done ? 0.7 : 1)};
`;

function CTAFooter({ progressStatus, updating, onAdvance, locked, lockedLabel, lockedHint }) {
  const done = progressStatus >= 4;
  const label = locked
    ? lockedLabel
    : bookingProgressStatusActionList[progressStatus || 0];
  return (
    <FooterBar>
      <FooterInner>
        <CtaButton
          onClick={done || locked ? undefined : onAdvance}
          disabled={updating || done || locked}
          $done={done}
        >
          <Icon path={locked ? ICON_PATHS.clock : ICON_PATHS.check} size={20} sw={2.2} />
          {label}
        </CtaButton>
        {locked && lockedHint && (
          <div
            style={{
              fontSize: 12,
              color: tokens.colors.muted,
              textAlign: "center",
              marginTop: 8,
            }}
          >
            {lockedHint}
          </div>
        )}
      </FooterInner>
    </FooterBar>
  );
}

// ────────────────────────────────────────────────────────────────
// Loading + error states
// ────────────────────────────────────────────────────────────────

const CenteredState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
`;

function LoadingState() {
  return (
    <CenteredState>
      <div style={{ color: tokens.colors.muted, fontSize: 14 }}>Chargement…</div>
    </CenteredState>
  );
}

function ErrorState({ message }) {
  return (
    <CenteredState>
      <div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
          {message || "Lien invalide ou expiré"}
        </div>
        <div style={{ fontSize: 13, color: tokens.colors.muted }}>
          Contactez l'équipe Zeplan si le problème persiste.
        </div>
      </div>
    </CenteredState>
  );
}

// ────────────────────────────────────────────────────────────────
// Orchestrator
// ────────────────────────────────────────────────────────────────

export default function TrackContent({ initialBooking }) {
  const { uid, token } = useParams();

  const [bookingRaw, setBookingRaw] = useState(initialBooking || null);
  const [comingBookingRaw, setComingBookingRaw] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState(null);

  const [comment, setComment] = useState(initialBooking?.driver_comment || "");
  const [savedComment, setSavedComment] = useState(
    initialBooking?.driver_comment || ""
  );

  const [updating, setUpdating] = useState(false);
  const _isUpdating = useRef(false);
  const [loadError, setLoadError] = useState(null);

  // Ticks every minute so the "Je suis en route" lock (2h-before-pickup window)
  // re-enables itself the moment we cross the threshold, without a page reload.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const [showCommentSheet, setShowCommentSheet] = useState(false);
  const [showDriverSheet, setShowDriverSheet] = useState(false);
  const [showBonSheet, setShowBonSheet] = useState(false);
  const [fetchingUpdateDriver, setFetchingUpdateDriver] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null);

  const { currentPos, currentPosInit, locationApiError } = useWatchPosition(
    !!bookingRaw?.driverJwt
  );

  const booking = useMemo(
    () => (bookingRaw ? convertBookingObj(bookingRaw) : {}),
    [bookingRaw]
  );

  const comingBookings = useMemo(
    () => comingBookingRaw.map((b) => convertBookingObj(b)),
    [comingBookingRaw]
  );

  const clientFullName = `${booking.firstname || ""} ${booking.lastname || ""}`;
  const isLaPremierePickup =
    isLaPremiereCdgName(clientFullName) && isCdgAddress(booking.pickup_address || "");
  const isLaPremiereDropoff =
    isLaPremiereCdgName(clientFullName) && isCdgAddress(booking.dropoff_address || "");

  const progressStatus = bookingRaw?.progress_status || 0;
  const noShow = !!bookingRaw?.noShow;
  const dirtyComment = (savedComment || "") !== (comment || "");

  // "Je suis en route" est verrouille tant qu'on n'est pas dans la fenetre des
  // 2h avant le pickup. Ne concerne que la 1re transition (0 -> 1). Fail-open si
  // la date du booking est invalide. Le backend applique le meme garde (source
  // de verite) ; ceci n'est que l'UX.
  const enRouteOpensAt =
    booking.pickup_date?.dateMoment?.isValid?.()
      ? booking.pickup_date.dateMoment.clone().subtract(2, "hours")
      : null;
  const enRouteLocked =
    progressStatus === 0 && !!enRouteOpensAt && enRouteOpensAt.isAfter(nowTick);

  const refreshBooking = useCallback(() => {
    setUpdating(true);
    _isUpdating.current = true;
    axios
      .get(apiRoot + "/api/v1/driverapp/track/" + uid + "/" + token)
      .then((res) => {
        setBookingRaw(res.data);
        setUpdating(false);
        _isUpdating.current = false;
      })
      .catch(() => {
        setUpdating(false);
        _isUpdating.current = false;
      });
  }, [token, uid]);

  // Initial fetch if the parent did not pass `initialBooking` already.
  useEffect(() => {
    if (bookingRaw) return undefined;
    let cancelled = false;
    axios
      .get(apiRoot + "/api/v1/driverapp/track/" + uid + "/" + token)
      .then((res) => {
        if (cancelled) return;
        setBookingRaw(res.data);
        setComment(res.data.driver_comment || "");
        setSavedComment(res.data.driver_comment || "");
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e?.response?.status || "unknown");
      });
    return () => {
      cancelled = true;
    };
  }, [bookingRaw, token, uid]);

  // Upcoming bookings — only once we know we're a driver.
  useEffect(() => {
    if (!bookingRaw?.driverJwt) return undefined;
    let cancelled = false;
    axios
      .get(
        apiRoot +
          "/api/v1/driverapp/track/coming/" +
          uid +
          "/" +
          token
      )
      .then((res) => {
        if (!cancelled) setComingBookingRaw(res.data || []);
      })
      .catch(() => {
        /* non-blocking */
      });
    return () => {
      cancelled = true;
    };
  }, [bookingRaw?.driverJwt, token, uid]);

  // Available drivers (only if the viewer is the company owning the driver).
  useEffect(() => {
    if (!bookingRaw?.driverJwt) return undefined;
    let cancelled = false;
    axios
      .get(
        apiRoot +
          `/api/v1/driverapp/track/availableDrivers/${uid}/${token}`
      )
      .then((res) => {
        if (cancelled) return;
        if (res.data && res.data.length) {
          setAvailableDrivers(
            res.data.map((d) => ({ value: d.id, label: d.name, data: d }))
          );
        }
      })
      .catch(() => {
        /* non-blocking */
      });
    return () => {
      cancelled = true;
    };
  }, [bookingRaw?.driverJwt, token, uid]);

  // ──────────────────────────────────────────────────────────────
  // Confirm helpers
  // ──────────────────────────────────────────────────────────────

  const askConfirm = useCallback(
    (cfg) =>
      new Promise((resolve) => {
        setPendingConfirm({ ...cfg, _resolve: resolve });
      }),
    []
  );
  const resolveConfirm = useCallback(
    (ok) => {
      const cur = pendingConfirm;
      setPendingConfirm(null);
      cur?._resolve?.(!!ok);
    },
    [pendingConfirm]
  );

  // ──────────────────────────────────────────────────────────────
  // Actions — each reuses an existing /api/v1/driverapp/track/* endpoint.
  // ──────────────────────────────────────────────────────────────

  const handleAdvance = useCallback(async () => {
    if (_isUpdating.current) return;
    if (progressStatus >= 4) return;
    if (enRouteLocked) return;

    const next = progressStatus + 1;
    const pickup = booking.pickup_address;
    const dropoff = booking.dropoff_address;
    const confirmConfig = (() => {
      if (next === 1)
        return {
          title: "Confirmer le départ",
          body: (
            <span>
              Vous partez vers <strong>{pickup}</strong>.
            </span>
          ),
          confirmLabel: "Je pars",
        };
      if (next === 2)
        return {
          title: "Confirmer votre arrivée",
          body: (
            <span>
              Vous êtes arrivé à <strong>{pickup}</strong>.
            </span>
          ),
          confirmLabel: "Je suis sur place",
        };
      if (next === 3)
        return {
          title: "Passagers à bord",
          body: (
            <span>
              Direction <strong>{dropoff}</strong>.
            </span>
          ),
          confirmLabel: "À bord",
        };
      return {
        title: "Confirmer la dépose",
        body: (
          <span>
            Vous déposez les passagers à <strong>{dropoff}</strong>.
          </span>
        ),
        confirmLabel: "Déposés",
      };
    })();

    const ok = await askConfirm(confirmConfig);
    if (!ok) return;

    setUpdating(true);
    _isUpdating.current = true;
    try {
      await axios.post(
        apiRoot +
          "/api/v1/driverapp/track/updateStatus/" +
          uid +
          "/" +
          token,
        { currentPos }
      );
      refreshBooking();
    } catch (e) {
      setUpdating(false);
      _isUpdating.current = false;
      if (e?.response?.status === 409 && e?.response?.data?.error === "TOO_EARLY") {
        window.alert(
          "Vous ne pouvez vous mettre en route que dans les 2h précédant le départ. Vérifiez que c'est bien la course du jour."
        );
      } else {
        window.alert(
          "Une erreur est survenue lors de la mise à jour du statut."
        );
      }
    }
  }, [
    askConfirm,
    booking.dropoff_address,
    booking.pickup_address,
    currentPos,
    token,
    uid,
    progressStatus,
    enRouteLocked,
    refreshBooking,
  ]);

  const handleStop = useCallback(async () => {
    if (_isUpdating.current) return;
    const ok = await askConfirm({
      title: "Signaler un arrêt",
      body: "Voulez-vous signaler un arrêt imprévu ?",
      confirmLabel: "Signaler",
    });
    if (!ok) return;
    setUpdating(true);
    _isUpdating.current = true;
    try {
      await axios.post(
        apiRoot +
          "/api/v1/driverapp/track/updateStop/" +
          uid +
          "/" +
          token,
        { currentPos }
      );
      window.alert("L'arrêt a bien été enregistré.");
    } catch (e) {
      window.alert("Une erreur est survenue.");
    } finally {
      setUpdating(false);
      _isUpdating.current = false;
    }
  }, [askConfirm, currentPos, token, uid]);

  const handleNoShow = useCallback(async () => {
    if (_isUpdating.current) return;
    const ok = await askConfirm({
      title: "Signaler un No Show",
      body: "Voulez-vous signaler un « No Show » pour cette course ?",
      confirmLabel: "Signaler",
      tone: "danger",
    });
    if (!ok) return;
    setUpdating(true);
    _isUpdating.current = true;
    try {
      await axios.post(
        apiRoot +
          "/api/v1/driverapp/track/updateNoShow/" +
          uid +
          "/" +
          token,
        { currentPos }
      );
      window.alert('Le "No Show" a bien été enregistré.');
      refreshBooking();
    } catch (e) {
      window.alert("Une erreur est survenue.");
      setUpdating(false);
      _isUpdating.current = false;
    }
  }, [askConfirm, currentPos, token, uid, refreshBooking]);

  const handleCancel = useCallback(async () => {
    const ok = await askConfirm({
      title: "Annuler la course",
      body:
        "Vous confirmez être dans l'incapacité à faire cette course ? Cette action est irréversible.",
      confirmLabel: "Annuler la course",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await axios.get(
        apiRoot +
          "/api/v1/driverapp/track/driverleave/" +
          uid +
          "/" +
          token
      );
      window.location.href = "/";
    } catch (e) {
      window.alert("Une erreur est survenue.");
    }
  }, [askConfirm, token, uid]);

  const handleSubmitComment = useCallback(async () => {
    try {
      await axios.post(
        apiRoot +
          "/api/v1/driverapp/track/updatecomment/" +
          uid +
          "/" +
          token,
        { text: comment }
      );
      setSavedComment(comment);
      setBookingRaw((old) => (old ? { ...old, driver_comment: comment } : old));
      window.alert("Le commentaire a bien été enregistré.");
    } catch (e) {
      window.alert("Une erreur est survenue.");
    }
  }, [comment, token, uid]);

  const handleUpdateDriver = useCallback(
    async (driver) => {
      if (!driver) return;
      setFetchingUpdateDriver(true);
      startLoading();
      try {
        const res = await axios.post(
          apiRoot +
            "/api/v1/driverapp/track/updateDriver/" +
            uid +
            "/" +
            token,
          { driver }
        );
        document.location.href = res.data;
      } catch (e) {
        setFetchingUpdateDriver(false);
        stopLoading();
        window.alert(
          "Une erreur est survenue lors de la mise à jour du chauffeur."
        );
        document.location.reload();
      }
    },
    [token, uid]
  );

  // ──────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <PageBg>
        <Column>
          <ErrorState />
        </Column>
      </PageBg>
    );
  }

  if (!bookingRaw) {
    return (
      <PageBg>
        <Column>
          <LoadingState />
        </Column>
      </PageBg>
    );
  }

  const pickupDateMoment = booking.pickup_date?.dateMoment?.locale("fr");
  const pickupHHmm = pickupDateMoment ? pickupDateMoment.format("HH:mm") : "—";
  const pickupLongDate = pickupDateMoment
    ? pickupDateMoment.format("dddd DD MMM YYYY")
    : "";
  const statusText = bookingProgressStatusList[progressStatus] || "En cours";
  const enRouteLockedHint =
    enRouteLocked && pickupDateMoment
      ? `Course prévue ${pickupDateMoment.format("dddd DD MMM à HH:mm")}`
      : "";

  return (
    <PageBg>
      <GeoWarning
        show={!!bookingRaw.driverJwt && !currentPosInit}
        locationApiError={locationApiError}
      />
      <Column>
        <Hero
          statusText={statusText}
          pickupHHmm={pickupHHmm}
          pickupLongDate={pickupLongDate}
          flightNumber={booking.flight_number}
          onBack={() => {
            window.history.back();
          }}
          appUrl={driverAppUrlFromTrack(uid, token)}
        />
        <Scroll>
          <CardsStack>
            <ClientCard
              firstname={booking.firstname}
              lastname={booking.lastname}
              phone={booking.phone}
            />
            <TripCard
              pickup={booking.pickup_address}
              dropoff={booking.dropoff_address}
              isLaPremierePickup={isLaPremierePickup}
              isLaPremiereDropoff={isLaPremiereDropoff}
            />
            <VehicleCard vehicle={booking.vehicle} />
            <InstructionsCard text={booking.additional_info} />
            <ActionsChips
              hasAvailableDrivers={!!(availableDrivers && availableDrivers.length)}
              noShowActive={noShow}
              updating={updating}
              dirtyComment={dirtyComment}
              onStop={handleStop}
              onNoShow={handleNoShow}
              onSwap={() => setShowDriverSheet(true)}
              onComment={() => setShowCommentSheet(true)}
              onCancel={handleCancel}
              onBonDeMission={() => setShowBonSheet(true)}
              calendarUrl={
                bookingRaw.driverJwt
                  ? `${apiRoot}/api/v1/driverapp/track/${uid}/${token}/calendar.ics`
                  : ""
              }
            />
            <UpcomingBookings bookings={comingBookings} />
          </CardsStack>
        </Scroll>
        <CTAFooter
          progressStatus={progressStatus}
          updating={updating}
          onAdvance={handleAdvance}
          locked={enRouteLocked}
          lockedLabel="Trop tôt pour démarrer"
          lockedHint={enRouteLockedHint}
        />
      </Column>

      <CommentSheet
        show={showCommentSheet}
        onHide={() => setShowCommentSheet(false)}
        value={comment}
        onChange={setComment}
        saved={savedComment}
        onSubmit={handleSubmitComment}
      />
      <UpdateDriverSheet
        show={showDriverSheet}
        onHide={() => setShowDriverSheet(false)}
        driverOptions={availableDrivers}
        booking={bookingRaw}
        fetching={fetchingUpdateDriver}
        submit={handleUpdateDriver}
      />
      <ConfirmSheet pending={pendingConfirm} onResolve={resolveConfirm} />
      <BonDeMissionSheet
        show={showBonSheet}
        onHide={() => setShowBonSheet(false)}
        bookingRaw={bookingRaw}
        booking={booking}
      />
    </PageBg>
  );
}
